import { useState, useEffect } from "react";
import { Menu, X, Phone, Mail } from "lucide-react";
import { COMPANY, buildWhatsAppUrl } from "../../lib/constants";

const NAV = [
  { label: "Leistungen", href: "#leistungen" },
  { label: "Bewertungen", href: "#bewertungen" },
  { label: "Angebot", href: "#angebot" },
];

export const Header = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Hero is now WHITE — keep dark text in both states
  const dark = false;

  return (
    <header
      data-testid="header"
      className={`fixed top-8 md:top-9 left-0 right-0 z-50 transition-all ${
        scrolled
          ? "bg-[#FAFAF7]/85 backdrop-blur-xl border-b border-black/10"
          : "bg-transparent border-b border-transparent"
      }`}
    >
      <div className="px-3 sm:px-4 md:px-8 lg:px-12 flex items-center justify-between h-16 md:h-20 gap-2">
        <a href="#top" data-testid="header-logo" className="flex items-center gap-2 sm:gap-3 group shrink-0">
          <div className="relative w-11 h-11 md:w-14 md:h-14 rounded-2xl bg-white border border-black/10 p-1.5 flex items-center justify-center overflow-hidden">
            {/* Rotating ring */}
            <span
              aria-hidden
              className="absolute inset-0 rounded-2xl border-2 border-transparent logo-spin"
              style={{
                background:
                  "conic-gradient(from 0deg, transparent 0deg, #25D366 60deg, transparent 120deg)",
                WebkitMask:
                  "linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0)",
                WebkitMaskComposite: "xor",
                maskComposite: "exclude",
                padding: "2px",
              }}
            />
            <img
              src={COMPANY.logo}
              alt="BRS"
              className="relative w-full h-full object-contain logo-pulse group-hover:scale-110 transition-transform duration-300"
            />
          </div>
          <span className="hidden md:flex flex-col leading-none">
            <span
              className={`font-semibold text-base md:text-lg tracking-tight ${
                dark ? "text-white" : "text-black"
              }`}
            >
              BREMER RECYCLING
            </span>
            <span
              className={`font-mono text-[10px] md:text-[11px] tracking-[0.25em] uppercase mt-1.5 ${
                dark ? "text-white/55" : "text-black/45"
              }`}
            >
              Service · Bremen
            </span>
          </span>
        </a>

        <nav data-testid="nav-links" className="hidden lg:flex items-center gap-7">
          {NAV.map((n) => (
            <a
              key={n.href}
              href={n.href}
              data-testid={`nav-${n.href.replace("#", "")}`}
              className={`text-sm transition-colors font-medium ${
                dark
                  ? "text-white/75 hover:text-white"
                  : "text-black/70 hover:text-black"
              }`}
            >
              {n.label}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-1.5 sm:gap-2">
          <a
            href={`tel:${COMPANY.phoneRaw}`}
            data-testid="header-phone-btn"
            className="inline-flex items-center gap-2 bg-black text-white hover:bg-black/85 px-3.5 sm:px-4 md:px-5 h-10 sm:h-11 rounded-full text-xs sm:text-sm font-semibold transition-colors"
            aria-label="Anrufen"
          >
            <Phone className="w-4 h-4" />
            <span className="hidden sm:inline">Anrufen</span>
          </a>
          <a
            href={`mailto:${COMPANY.email}?subject=Anfrage%20Bremer%20Recycling%20Service`}
            data-testid="header-mail-btn"
            className="inline-flex items-center justify-center bg-white text-black border border-black/15 hover:bg-black hover:text-white hover:border-black w-10 h-10 sm:w-11 sm:h-11 rounded-full transition-colors"
            aria-label="E-Mail · info@bremer-recycling-service.de"
            title="E-Mail · info@bremer-recycling-service.de"
          >
            <Mail className="w-4 h-4" />
          </a>
          <a
            href={buildWhatsAppUrl()}
            target="_blank"
            rel="noreferrer"
            data-testid="header-whatsapp-btn"
            className="inline-flex items-center gap-2 btn-whatsapp px-3.5 sm:px-4 md:px-5 h-10 sm:h-11 rounded-full text-xs sm:text-sm font-semibold"
          >
            <WhatsAppIcon />
            <span className="hidden sm:inline">WhatsApp</span>
          </a>
          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setOpen(!open)}
            className="lg:hidden inline-flex items-center justify-center w-10 h-10 sm:w-11 sm:h-11 rounded-full transition-colors btn-ghost"
            aria-label="Menü"
          >
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {open && (
        <div
          data-testid="mobile-menu"
          className="lg:hidden border-t border-black/10 bg-[#FAFAF7]/95 backdrop-blur-xl"
        >
          <div className="flex flex-col">
            {NAV.map((n) => (
              <a
                key={n.href}
                href={n.href}
                onClick={() => setOpen(false)}
                className="px-6 py-4 border-b border-black/5 text-sm text-black/80 hover:bg-black/5"
              >
                {n.label}
              </a>
            ))}
            <a
              href={`tel:${COMPANY.phoneRaw}`}
              className="px-6 py-4 border-b border-black/5 text-sm text-black/80"
            >
              {COMPANY.phone}
            </a>
          </div>
        </div>
      )}
    </header>
  );
};

export const WhatsAppIcon = ({ className = "w-4 h-4" }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M.057 24l1.687-6.163a11.867 11.867 0 0 1-1.587-5.946C.16 5.335 5.495 0 12.05 0a11.82 11.82 0 0 1 8.413 3.488 11.82 11.82 0 0 1 3.48 8.414c-.003 6.554-5.338 11.89-11.893 11.89a11.9 11.9 0 0 1-5.688-1.448L.057 24zm6.597-3.807c1.676.995 3.276 1.591 5.392 1.592 5.448 0 9.886-4.434 9.889-9.885.002-5.462-4.415-9.89-9.881-9.892-5.452 0-9.887 4.434-9.889 9.884-.001 2.225.651 3.891 1.746 5.634l-.999 3.648 3.742-.981zm11.387-5.464c-.074-.124-.272-.198-.57-.347-.297-.149-1.758-.868-2.031-.967-.272-.099-.47-.149-.669.149-.198.297-.768.967-.941 1.165-.173.198-.347.223-.644.074-.297-.149-1.255-.462-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.521.149-.173.198-.297.297-.495.099-.198.05-.372-.025-.521-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.501-.669-.51l-.57-.01c-.198 0-.52.074-.792.372s-1.04 1.016-1.04 2.479 1.065 2.876 1.213 3.074c.149.198 2.095 3.2 5.076 4.487.709.306 1.263.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413z" />
  </svg>
);
